import { Grid } from '@mantine/core';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { LogoDimensions } from 'src/components/common/MarketplaceLogo';
import { ListingPriceInput } from 'src/components/Input/ListingPriceInput';
import { Content } from 'src/contexts/ContentContext';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Stack, Switch } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { roundToPrecision } from 'src/utils/numberFormatter';
import {
  ListingDetailsUpdateInput,
  MarketplaceAccountSetting,
  UiCurrency,
} from 'src/WebApiController';

import * as styles from './BulkEditMarketplacePriceMobileContent.css';

export const StubHubManualPrice = ({
  MarketplaceLog,
  disabled,
  currency,
  marketplaceAccountSettings,
  onPriceChanged,
}: {
  MarketplaceLog: React.FC<LogoDimensions>;
  disabled: boolean;
  currency: UiCurrency;
  marketplaceAccountSettings?: MarketplaceAccountSetting;
  onPriceChanged: () => void;
}) => {
  return (
    <Stack direction="column" width="full" gap="l">
      <Grid align="center" style={{ fontSize: vars.typography.fontSize.sm }}>
        <Grid.Col offset={1}>
          <Content id={ContentId.StubHubPrice} />
        </Grid.Col>
      </Grid>
      <Grid align="center" className={styles.priceTileContainer}>
        <Grid.Col span={1}>
          <MarketplaceLog width={24} height={24} />
        </Grid.Col>
        <Grid.Col span={11}>
          <ListingPriceInput
            isBulkEdit
            currencyCode={currency.code}
            rootClassName={styles.priceInput}
            disabled={disabled}
            isAllInPrice
            field="allInPrice"
            otherField="listPrice"
            fieldToOtherRatio={
              1 / (1 - (marketplaceAccountSettings?.sellerFee ?? 0))
            }
            onChange={(_) => onPriceChanged()}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export const MarketplacePriceInputHeader = () => {
  return (
    <Grid align="center" style={{ fontSize: vars.typography.fontSize.sm }}>
      <Grid.Col offset={1} span={5}>
        <Content id={ContentId.Price} />
      </Grid.Col>
      <Grid.Col span={3.5}>
        <Content id={ContentId.Markup} />
      </Grid.Col>
      <Grid.Col span={2.5}>
        <Content id={ContentId.Override} />
      </Grid.Col>
    </Grid>
  );
};

export const MarketplacePriceInputTile = ({
  MarketplaceLog,
  inputIndex,
  currency,
  marketplaceAccountSettings,
  disabled,
  onPriceEdit,
}: {
  inputIndex: number;
  MarketplaceLog: React.FC<LogoDimensions>;
  disabled: boolean;
  currency: UiCurrency;
  marketplaceAccountSettings?: MarketplaceAccountSetting;
  onPriceEdit: (
    newPrice: number,
    pricyByMarketplace: boolean,
    index: number
  ) => void;
}) => {
  const { watch, setValue, register } =
    useFormContext<ListingDetailsUpdateInput>();

  const priceByMarketplaceEnabled = watch(
    `marketplacePriceUpdates.${inputIndex}.priceByMarketplace`
  );
  const listPrice = watch('listPrice');

  const defaultMarkup = useMemo(() => {
    return marketplaceAccountSettings?.markup != null
      ? roundToPrecision(marketplaceAccountSettings.markup * 100, 2)
      : null;
  }, [marketplaceAccountSettings?.markup]);

  return (
    <Grid align="center" className={styles.priceTileContainer}>
      <Grid.Col span={1}>
        <MarketplaceLog width={24} height={24} />
      </Grid.Col>
      <Grid.Col span={5}>
        <ListingPriceInput
          rootClassName={styles.priceInput}
          isBulkEdit
          currencyCode={currency.code}
          disabled={disabled || !priceByMarketplaceEnabled}
          isAllInPrice
          field={`marketplacePriceUpdates.${inputIndex}.allInPrice`}
          otherField={`marketplacePriceUpdates.${inputIndex}.listPrice`}
          fieldToOtherRatio={
            1 / (1 - (marketplaceAccountSettings?.sellerFee ?? 0))
          }
          onChange={(newPrice) =>
            onPriceEdit(newPrice, priceByMarketplaceEnabled, inputIndex)
          }
        />
      </Grid.Col>
      <Grid.Col span={3.5}>
        <PosTextField
          rootProps={{
            className: styles.percentageInput,
          }}
          disabled={disabled || Boolean(priceByMarketplaceEnabled)}
          type="number"
          placeholder={`${defaultMarkup}`}
          postfixDisplay="%"
          {...register(`marketplacePriceUpdates.${inputIndex}.markup`)}
        />
      </Grid.Col>
      <Grid.Col span={2.5}>
        <Switch
          disabled={disabled}
          checked={priceByMarketplaceEnabled}
          onCheckedChange={(isChecked: boolean) => {
            setValue(
              `marketplacePriceUpdates.${inputIndex}.priceByMarketplace`,
              isChecked
            );
            if (!isChecked) {
              // If this is false, set the value to listValue
              const mpListPrice = roundToPrecision(
                (listPrice ?? 0) *
                  (1 + (marketplaceAccountSettings?.markup ?? 0)),
                currency.dec
              );
              setValue(
                `marketplacePriceUpdates.${inputIndex}.listPrice`,
                mpListPrice
              );
            }
            setValue(`marketplacePriceUpdates.${inputIndex}.markup`, null);
          }}
        />
      </Grid.Col>
    </Grid>
  );
};
